import { Helmet } from 'react-helmet-async';
import { localBusinessSchema, faqPageSchema, SITE_URL } from '../lib/seoSchemas';
import Hero from '../components/Hero';
import TrustedByStrip from '../components/TrustedByStrip';
import EventTypeCards from '../components/EventTypeCards';
import WhyChooseUs from '../components/WhyChooseUs';
import OfferingsSection from '../components/OfferingsSection';
import GalleryHighlight from '../components/GalleryHighlight';
import TestimonialCarousel from '../components/TestimonialCarousel';
import FullDayBlock from '../components/FullDayBlock';
import FinalCTA from '../components/FinalCTA';
import FAQAccordion from '../components/FAQAccordion';
import AnimatedSection from '../components/AnimatedSection';
import {
  DividerFootprints,
  DividerClaws,
  DividerBones,
} from '../components/DinoDecorations';
import { JungleBorder } from '../components/JungleBorder';

const HOME_FAQS = [
  {
    question: 'Is Jurassic Jack suitable for large agricultural shows?',
    answer: 'Yes. Jurassic Jack is designed specifically for outdoor regional events and busy showgrounds, with roaming encounters and activation zones that scale for large crowds.',
  },
  {
    question: 'Do you travel outside Geelong?',
    answer: 'Yes. We\u2019re based in Geelong and travel across Victoria. For regional events, travel and accommodation (if required) are included in the quote.',
  },
  {
    question: 'Is it scary for younger kids?',
    answer: 'No. Encounters are friendly and age-appropriate. We adapt the tone and interaction level to suit your audience.',
  },
  {
    question: 'Do you need a stage or PA system?',
    answer: 'Not usually. Jurassic Jack is designed as a roaming activation and interactive experience that works across the grounds.',
  },
  {
    question: 'How do we book?',
    answer: 'Click \u201CRequest a Quote\u201D and tell us your dates, location and event type. We\u2019ll confirm availability and recommend the best setup.',
  },
];

const Home = () => {
  return (
    <>
      <Helmet>
        <title>Jurassic Jack – RoARsOME Dinosaur Adventure for Shows, Festivals & Schools (VIC)</title>
        <meta
          name="description"
          content="Premium roaming dinosaur entertainment for agricultural shows, festivals, schools and community events. All-day interactive experiences, fossil activities and unforgettable photo moments. Request a quote."
        />
        <meta property="og:title" content="Jurassic Jack – RoARsOME Dinosaur Adventure" />
        <meta property="og:description" content="Premium roaming dinosaur entertainment for agricultural shows, festivals, schools and community events across Victoria." />
        <meta property="og:image" content={`${SITE_URL}/images/crowd-festival-dinosaur-enthusiasts-007.jpeg`} />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jurassic Jack – RoARsOME Dinosaur Adventure" />
        <meta name="twitter:description" content="Premium roaming dinosaur entertainment for agricultural shows, festivals, schools and community events across Victoria." />
        <meta name="twitter:image" content={`${SITE_URL}/images/crowd-festival-dinosaur-enthusiasts-007.jpeg`} />
        <script type="application/ld+json">{localBusinessSchema()}</script>
        <script type="application/ld+json">{faqPageSchema(HOME_FAQS)}</script>
      </Helmet>
      <Hero />
      <TrustedByStrip />

      {/* Event Types */}
      <div className="relative overflow-hidden">
        <JungleBorder side="left" />
        <JungleBorder side="right" />
        <EventTypeCards />
      </div>

      {/* What Jurassic Jack Delivers */}
      <section className="py-20 bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-10">
            <p className="text-base text-cream/50 font-mono uppercase tracking-wider">What We Deliver</p>
            <h2 className="text-4xl md:text-5xl font-display text-cream">
              Interactive Entertainment Families Don't Just Watch —{' '}
              <span className="text-orange-vibrant">They Join</span>
            </h2>
            <p className="mt-4 text-base text-cream/60 max-w-2xl mx-auto">
              Jurassic Jack is designed to pull people in. Kids participate, families follow, crowds gather — and your event gains an attraction that feels alive across the day.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <ul className="space-y-4 max-w-xl mx-auto">
              {[
                'Roaming dinosaur and creature encounters',
                'Hands-on fossil dig activities and discovery zones',
                'Interactive storytelling and crowd engagement',
                'Highly visual activation spaces that draw families in',
                'Memorable photo opportunities that spread online organically',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-amber/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-amber" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-cream/70">{item}</span>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      <WhyChooseUs />

      <OfferingsSection />

      {/* Gallery section */}
      <DividerClaws />
      <div className="relative overflow-hidden">
        <JungleBorder side="left" />
        <JungleBorder side="right" />
        <GalleryHighlight />
      </div>

      <DividerBones />
      <TestimonialCarousel />

      {/* Full Day */}
      <div className="relative overflow-hidden">
        <JungleBorder side="left" />
        <JungleBorder side="right" />
        <FullDayBlock />
      </div>

      <DividerFootprints className="rotate-180" />

      {/* Available Across Victoria */}
      <section className="py-12 bg-cream">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <AnimatedSection>
            <p className="font-mono text-sm uppercase tracking-[0.2em] text-jp-red mb-2">Service Area</p>
            <h2 className="text-3xl md:text-4xl font-display text-charcoal mb-4">
              Available Across <span className="text-amber">Victoria</span>
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              Based in Geelong, Jurassic Jack travels across Victoria for agricultural shows, festivals, schools and community events. Travel and accommodation are included in the quote when required.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Home FAQ */}
      <FAQAccordion
        heading="Frequently Asked Questions"
        items={HOME_FAQS}
      />

      <FinalCTA />
    </>
  );
};

export default Home;
